/**
 * English UI strings. Help has its blueprint EN/BM content separately;
 * a global Bahasa Melayu UI catalogue remains a clinic decision.
 */
export const en = {
  app: { name: 'Ommri Care Admin' },
  billing: {
    chooseBranch: 'Choose the branch you are working at in the branch switch.',
    chooseBranchToCreate: 'Choose a branch to create a new bill.',
    summaryScope: 'Billing totals · {{scope}}',
    summaryFailed: 'Billing totals could not be loaded',
    selectBillBranch: 'Select {{branch}} for bill actions',
  },
  doctorPhoto: {
    label: 'Doctor photo',
    hint: 'JPEG or PNG, up to 600 KB. Upload saves the photo immediately.',
    none: 'No photo',
    upload: 'Upload photo',
    remove: 'Remove photo',
    type: 'Choose a JPEG or PNG photo.',
    size: 'Choose a photo no larger than 600 KB.',
    invalid: 'The photo is invalid. Choose a valid JPEG or PNG.',
    readFailed: 'Could not read the photo. Try another file.',
  },
  patientAppSignIn: {
    title: 'App sign-in methods',
    loading: 'Loading app sign-in methods',
    loadFailed: 'App sign-in methods could not be loaded.',
    noLongerAvailable: 'This app account is no longer linked to the patient.',
    empty: 'No app sign-in methods found',
    emptyBody:
      'This patient record does not currently have an app account with sign-in methods.',
    retry: 'Try again',
    phone: 'Phone',
    verified: 'Verified',
    phoneUnavailable: 'Verified phone unavailable',
    email: 'Email',
    confirmed: 'Confirmed',
    emailNotSet: 'Not set',
    password: 'Password',
    passwordSet: 'Set',
    passwordNotSet: 'Not set',
    passwordDisabled: 'Disabled by clinic',
    setAt: 'Set {{time}}',
    disabledAt: 'Disabled {{time}}',
    disabledReason: 'Reason: {{reason}}',
    lockedUntil: 'Locked until {{time}}',
    disable: 'Turn off password sign-in',
    enable: 'Allow password sign-in',
    disableTitle: 'Turn off password sign-in',
    enableTitle: 'Allow password sign-in',
    disableBody:
      'The password will be erased. Phone sign-in and existing sessions are unchanged. The patient cannot set another password until staff allow it again.',
    enableBody:
      'This lets the patient set a new password in the app. The old password is not restored.',
    reason: 'Reason',
    reasonHint:
      'Required and recorded in the audit log. {{min}}–{{max}} characters.',
    reasonTooShort: 'Enter a reason of at least {{min}} characters.',
    cancel: 'Cancel',
    disableSubmit: 'Turn off password sign-in',
    enableSubmit: 'Allow password sign-in',
    disabledToast: 'Password sign-in turned off',
    enabledToast: 'Password sign-in allowed',
    disabledToastBody:
      'The password was erased. The patient can still sign in with their phone.',
    enabledToastBody: 'The patient can now set a new password in the app.',
    changedElsewhere: 'The app account changed',
    changedElsewhereBody:
      'Reloaded the sign-in methods. Check the current state before trying again.',
    permissionDenied:
      'Your role cannot change password sign-in for app accounts.',
    changeFailed: 'Password sign-in could not be changed. Try again.',
  },
  dispensary: {
    controlledDrugsTitle: 'Controlled-drug register',
    controlledDrugsTab: 'Controlled drugs',
    controlledDrugsChooseBranch:
      'Choose a branch to view its controlled-drug registers.',
    controlledDrugsPoison: 'Poison prescription book',
    controlledDrugsControlled: 'Controlled-drug register',
    controlledDrugsNoPoison: 'No poison-register entries for this branch.',
    controlledDrugsNoControlled:
      'No controlled-drug register entries for this branch.',
    controlledDrugsIn: 'In',
    controlledDrugsOut: 'Out',
    controlledDrugsWitness: 'Witness',
    poisonGroup: 'Group {{group}} poison',
    cdKind: {
      opening: 'Opening balance',
      supply: 'Supplied',
      receipt: 'Received',
      transfer_in: 'Transfer in',
      transfer_out: 'Transfer out',
      return_supplier: 'Returned to supplier',
      write_off: 'Written off',
      count_adjust: 'Stock count',
      correction: 'Correction',
    },
    cdCorrects: 'Corrects entry {{entry}}: {{reason}}',
    cdCorrectedBy: 'Corrected by entry {{entries}}',
    cdCorrect: 'Correct entry',
    cdNever:
      'Entries are never edited. A correction is a new entry linked to the one it corrects, and the stock moves by the difference.',
    cdCorrectTitle: 'Correct entry {{entry}} · {{medicine}}',
    cdCorrectIntro:
      'This entry reads {{quantity}} ({{direction}}). Enter what it should have read. A new linked entry is added; this one stays as it is.',
    cdCorrectQuantity: 'Correct quantity',
    cdCorrectReason: 'Reason',
    cdCorrectReasonHint: 'At least 5 characters. Kept with the correction.',
    cdWitness: 'Witness',
    cdChooseWitness: 'Choose a dispenser or doctor of this branch',
    cdDiscrepancyBadge: 'Check',
    cdDiscrepancy:
      '{{medicine}}: the register says {{balance}} but {{onHand}} are in stock. Count it and record the difference.',
    cdNotInRegister:
      '{{medicine}}: {{onHand}} in stock but no register entry yet.',
    cdCollectedByPatient: 'Collected by the patient · ID ending {{last4}}',
    cdCollectedBy: 'Collected by {{name}} · ID ending {{last4}}',
    cdCorrectSave: 'Add correction',
    cdCorrected: 'Correction recorded as entry {{entry}}',
    cdCorrectFailed: 'The correction was not recorded',
    controlledDrugsNumber: 'Entry',
    controlledDrugsDate: 'Supplied',
    controlledDrugsMedicine: 'Medicine',
    controlledDrugsPatient: 'Patient',
    controlledDrugsPrescriber: 'Prescriber',
    controlledDrugsQuantity: 'Quantity',
    controlledDrugsBalance: 'Balance',
    safetyColumn: 'Safety checks',
    safetyChecksDue: 'Safety checks due',
    safetyChecksComplete: 'Safety checks complete',
    scanControl: 'Scan / manual scan',
    clinicalControl: 'Clinical verification',
    counsellingControl: 'Counselling',
    completeChecksFirst: 'Complete and record every safety check first',
    safetyTitle: 'Dispensing safety checks — {{number}}',
    safetyIntro:
      'Confirm every check before recording this prescription as ready to dispense.',
    scanMethod: 'Medicine scan method',
    scanMedicines: 'Scan medicines',
    manualScan: 'Manual scan confirmation',
    scanHelp:
      'Use manual confirmation only when the scanner is unavailable. Batch and expiry checks remain required.',
    clinicalVerified: 'Clinical verification completed',
    counsellingDone: 'Patient counselling completed',
    identityChecked: 'Patient identity checked for handover',
    recordChecks: 'Record safety checks',
    checksRecorded: 'Safety checks recorded',
    stillMissing: 'Recorded. Still to do before dispensing: {{list}}.',
    untickedHint: 'Tick {{list}} first.',
    refillBadge: 'Refill',
    dispensedTab: 'Dispensed',
    yes: 'Yes',
    no: 'No',
    dispensedTitle: 'Dispensed prescriptions',
    dispensedSub: 'Handed over, packed or delivered',
    dispensedCaption: 'Dispensed prescriptions',
    dispensedDay: 'Day',
    dispensedAt: 'Dispensed',
    noneDispensed: 'Nothing dispensed on this day',
    viewDetails: 'View details',
    reprintLabels: 'Reprint labels',
    dispensedStatus: {
      dispensed: 'Dispensed',
      delivery_packed: 'Packed for delivery',
      delivered: 'Delivered',
    },
    idCheckedPatient: 'the patient, ID ending {{last4}}',
    idCheckedOther: '{{name}}, ID ending {{last4}}',
    refillOf: 'Refill of {{number}}',
    refillRequest: 'Approved refill request {{id}}… · {{when}}',
    missing: {
      clinical: 'clinical verification',
      scan: 'scan every medicine',
      counselling: 'counselling',
      idCheck: 'the ID check',
      secondCheck: 'the second checker',
    },
    badgeIdDue: 'ID check due',
    badgeIdDone: 'ID checked',
    badgeSecondDue: 'Second check due',
    badgeSecondDone: 'Second check done',
    idCheckTitle: 'ID check at hand-over',
    idCheckHelp:
      'Controlled or psychotropic medicine: check the ID of the person collecting it.',
    collector: 'Who is collecting',
    collectorPatient: 'The patient',
    collectorOther: 'Someone else',
    collectorName: 'Name of the person collecting',
    idLast4: 'Last 4 digits of the ID seen',
    idLast4Hint: 'MyKad digits, or the last 4 characters of a passport.',
    secondCheckTitle: 'Second checker',
    secondCheckHelp:
      'High-alert, controlled or psychotropic medicine: another dispenser or doctor of this branch checks it with you.',
    secondChecker: 'Second checker',
    checkerPin: "Second checker's PIN",
    checkerPinHint:
      'The second checker types their own unlock PIN here. If they have none, they set one in their profile first.',
    chooseChecker: 'Choose a dispenser or doctor',
    checkersLoading: 'Loading…',
    checkersFailed: 'The list of second checkers could not be loaded.',
    noCheckers: 'No other dispenser or doctor works at this branch.',
    checkerOption: '{{name}} ({{kind}})',
    checkerOptionAway: '{{name}} ({{kind}}, not signed in)',
    checkerKind: { dispenser: 'dispenser', doctor: 'doctor' },
    scanned: 'Scanned',
    awaitingScan: 'Awaiting scan',
    scanProgress: '{{done}} of {{total}} scanned',
    scanItem: 'Medicine',
    scanCode: 'Scan the box',
    scanCodeHint:
      'Scan the barcode (it ends with Enter), or type the batch number and press Enter.',
    scanRecord: 'Record scan',
    scanOk: '{{medicine}} scanned.',
    scanFailed: 'The scan was not recorded. Try again.',
    scannedBatch: 'Scanned · {{batch}}',
    scannedManual: 'Confirmed by hand · {{batch}}',
    itemScannedOption: '{{medicine}} (scanned)',
    manualBatch: 'Batch number on the box',
    manualReason: 'Why the scanner was not used',
    manualReasonHint: 'For example: the barcode does not read.',
    manualRecord: 'Confirm by hand',
    attester: 'Second person confirming the box',
    attesterHint:
      'Controlled, psychotropic or high-alert medicine: another dispenser or doctor checks the box and types their own PIN.',
    attesterPin: "Second person's PIN",
  },
  outOfStock: {
    tab: 'Out of stock',
    openRecord: 'Open patient record',
    changeClosedHint:
      'The visit is closed. A replacement prescription is written at a new visit.',
    title: 'Out of stock',
    show: 'Show',
    status: { open: 'Not seen yet', seen: 'Seen', all: 'All' },
    empty: 'No out-of-stock notices.',
    patient: 'Patient',
    medicines: 'Medicines',
    note: 'Dispensary note',
    state: 'State',
    actions: 'Actions',
    notifiedBy: 'From {{name}} · {{when}}',
    prescriber: 'Prescriber: {{name}}',
    seenOn: 'Seen {{when}}',
    change: 'Change prescription',
    markSeen: 'Mark seen',
    markedSeen: 'Marked seen',
    notDone: 'Not done',
  },
  team: {
    chooseBranch: 'Choose a branch to see its team messages.',
    notice:
      "Hand-offs and questions about patients happen here, not on personal WhatsApp, so results and photos stay inside the clinic's records.",
    threads: 'Threads',
    new: 'New',
    empty: 'No threads yet',
    emptyBody: 'Start one about a patient or with a desk.',
    write: 'Write a message',
    send: 'Send',
    newThread: 'New thread',
    start: 'Start thread',
    about: 'About',
    patient: 'A patient',
    desk: 'A desk',
    deskLabel: 'Desk',
    chooseDesk: 'Choose a desk',
    people: 'People',
    message: 'Message',
  },
  help: {
    search: 'Search help',
    language: 'Language',
    noMatches: 'No guides match',
  },
  government: {
    title: 'Government schemes',
    madani: 'Skim Perubatan MADANI',
    peka_b40: 'PeKa B40',
    perkeso_case: 'PERKESO',
    madaniBody:
      'MADANI eligibility checks and claims are not connected. No eligibility, entitlement, current limits or payment outcome is assumed. Use the approved ProtectHealth process until the clinic configures an integration.',
  },
  schedules: {
    myTitle: 'My weekly schedule',
    managerTitle: 'Weekly hours',
    notice: 'Only the clinic manager and the Super admin can change these',
    weekly: 'Regular week',
    overrides: 'Date changes',
    noShift: 'Not scheduled',
    noOverrides: 'No date changes',
    noOverridesBody: 'Your regular weekly schedule applies.',
    branch: 'Branch',
    changeRequest: 'Request a schedule change',
    changeRequestBody:
      'Send the clinic manager the date and details of the change you need.',
    requestKind: 'Request type',
    requestKindPlaceholder: 'Choose a request type',
    requestKinds: {
      swap_shift: 'Swap shift',
      leave: 'Leave',
      hours_change: 'Change hours',
      other: 'Other',
    },
    date: 'Date',
    details: 'Details',
    detailsPlaceholder: 'Describe the schedule change you need',
    submit: 'Send request',
    submitted: 'Schedule change request sent',
    validationError: 'Complete every request field.',
    submitFailed: 'Request not sent',
    schedulesTab: 'Schedules',
    requestsTab: 'Requests',
    doctor: 'Doctor',
    chooseDoctor: 'Choose a doctor',
    doctorRequired: 'Choose a doctor.',
    dayOrDate: 'Day / date',
    scheduleType: 'Schedule type',
    scheduleKinds: {
      weekly_template: 'Regular week',
      date_override: 'Date change',
    },
    weekday: 'Day',
    hours: 'Hours',
    startTime: 'Start time',
    endTime: 'End time',
    invalidHours: 'Choose an end time after the start time.',
    dateRequired: 'Choose a date.',
    actions: 'Actions',
    edit: 'Edit',
    addSchedule: 'Add schedule',
    editSchedule: 'Edit schedule',
    delete: 'Delete',
    confirmDelete: 'Tap again to delete',
    scheduleCreated: 'Schedule added',
    scheduleUpdated: 'Schedule updated',
    scheduleDeleted: 'Schedule deleted',
    noSchedules: 'No doctor schedules yet',
    noSchedulesBody: 'Add a regular shift or a date change for a doctor.',
    pendingRequests: 'Pending requests',
    decidedRequests: 'Decided requests',
    noPendingRequests: 'No pending requests',
    noDecidedRequests: 'No decided requests',
    approve: 'Approve',
    decline: 'Decline',
    status: 'Status',
    requestApproved: 'Schedule request approved',
    requestDeclined: 'Schedule request declined',
    requestStatuses: {
      pending: 'Pending',
      approved: 'Approved',
      declined: 'Declined',
    },
    scheduleStale: 'This schedule no longer exists.',
    requestStale: 'This request has already changed or no longer exists.',
    staleGuidance: 'The latest schedules and requests are being reloaded.',
    weekdays: {
      0: 'Sunday',
      1: 'Monday',
      2: 'Tuesday',
      3: 'Wednesday',
      4: 'Thursday',
      5: 'Friday',
      6: 'Saturday',
    },
  },
  attendance: {
    addRecord: 'Add attendance record',
    addRecordHint:
      'For a day the person worked but did not clock (up to 31 days back). Days in an approved or paid locum pay week cannot be changed.',
    addRecordRequired:
      'Fill in the person, branch, day, both times and the reason.',
    choosePerson: 'Choose the person',
    chooseBranch: 'Choose the branch',
    recordAdded: 'Attendance record added',
    title: 'Attendance',
    managerTitle: 'Manager attendance',
    today: "Today's attendance",
    branch: 'Branch',
    date: 'Date',
    staff: 'Staff member',
    status: 'Status',
    actions: 'Actions',
    ready: 'Ready to start your shift.',
    since: 'Clocked in since {{time}}',
    topbarSince: 'In since {{time}}',
    completedTimes: '{{start}}–{{end}}',
    clockIn: 'Clock in',
    clockOut: 'Clock out',
    clockedIn: 'Clocked in',
    clockedOut: 'Clocked out',
    clockOutTitle: 'Confirm clock-out',
    totalWorked: 'Total time worked',
    durationHoursMinutes: '{{hours}}h {{minutes}}m',
    durationMinutes: '{{minutes}}m',
    late: 'Late',
    early: 'Left early',
    lateReasonTitle: 'Reason for late clock-in',
    earlyReasonTitle: 'Reason for early clock-out',
    reason: 'Reason',
    reasonHint: 'This is kept with the attendance record.',
    reasonRequired: 'Enter a reason.',
    myHistory: 'My attendance history',
    todayTab: 'Today',
    historyTab: 'History',
    missingTab: 'Missing clock-outs',
    schedulesTab: 'Schedules',
    noHistory: 'No attendance recorded yet',
    noSchedules: 'No schedules configured',
    scheduleDay: 'Day',
    scheduledHours: 'Scheduled hours',
    missingClockOut: 'Missing clock-out',
    missingSummary: '{{count}} attendance records need a clock-out correction.',
    allBranches: 'All branches',
    correct: 'Correct',
    resolve: 'Resolve',
    correctTitle: 'Correct attendance',
    resolveMissing: 'Resolve missing clock-out',
    correctionReason: 'Correction reason',
    clockOutHint: 'Leave blank only if the shift is still in progress.',
    timeRequired: 'Enter the clock-in time.',
    corrected: 'Attendance corrected',
    correctionProvenance: 'Correction history',
    originalTimestamp: 'Original: {{value}}',
    correctedTimestamp: 'Manager corrected: {{value}}',
    stateChanged: 'Attendance status changed',
    states: {
      not_clocked_in: 'Not clocked in',
      clocked_in: 'Clocked in',
      complete: 'Complete',
      missing_clock_out: 'Missing clock-out',
    },
  },
  staffInvites: {
    roles: { locum: 'Locum' },
    mmcNumber: 'MMC number (doctors and locums)',
  },
  staff: {
    view: 'View staff',
    status: 'Status',
    twoFactor: '2FA',
    enabled: 'On',
    notEnabled: 'Off',
    locumContracts: 'Locum contracts',
    addContract: 'Add contract',
    editContract: 'Edit contract',
    contractSaved: 'Locum contract saved',
    noContractBody:
      'Add contract terms to this staff record before the first shift.',
    resetTwoFactor: 'Reset two-factor',
    resetTwoFactorTitle: 'Reset two-factor · {{name}}',
    resetTwoFactorBody:
      'This clears the authenticator setup and every recovery code, ends this staff member’s active sessions, and requires two-factor setup at their next sign-in.',
    resetReason: 'Reason for reset',
    resetReasonHint:
      'Required. This reason is recorded in the audit log (maximum 500 characters).',
    resetReasonPlaceholder: 'e.g. Staff member lost their authenticator phone',
    confirmResetTwoFactor: 'Confirm reset',
    resetComplete: 'Two-factor reset complete',
    resetCompleteBody:
      '{{sessions}} active session(s) ended and {{codes}} recovery code(s) revoked. The staff member must set up two-factor at their next sign-in.',
    resetErrors: {
      superAdminOnly:
        'Switch to the Super admin role before resetting two-factor.',
      selfReset:
        'You cannot reset your own two-factor. Ask another Super admin.',
      notFound:
        'This staff member no longer exists. Close this record and refresh the list.',
      invalidReason: 'Enter a reason of no more than 500 characters.',
      generic: 'Two-factor was not reset. Try again.',
    },
  },
  staffSessions: {
    signedInDevices: 'Signed-in devices',
    signedInDevicesHint: 'See and sign out browsers using your account',
    ownIntro:
      'These are the browsers and devices currently associated with your account. Sign out any device you no longer recognise or use.',
    pageTitle: 'Staff sessions',
    staffDirectory: 'Staff available for session management',
    staffMember: 'Staff member',
    actions: 'Actions',
    viewSessions: 'View sessions',
    staffTitle: 'Sessions · {{name}}',
    staffIntro:
      'Review this staff member’s devices and remotely end sessions that should no longer have access.',
    loadingSessions: 'Loading signed-in devices',
    noSessions: 'No signed-in devices',
    noSessionsBody: 'No unexpired browser or device sessions were found.',
    thisDevice: 'This device',
    signedOut: 'Signed out',
    ipAddress: 'IP address',
    signedIn: 'Signed in',
    lastActive: 'Last active',
    expires: 'Expires',
    signOut: 'Sign out',
    currentSessionHint: 'Use the main Sign out button for this device.',
    signOutDeviceTitle: 'Sign out this device?',
    signOutDeviceBody:
      '{{target}} will need to sign in again before it can access Ommri Care Admin.',
    confirmSignOut: 'Sign out device',
    signOutEverywhere: 'Sign out everywhere',
    signOutEverywhereTitle: 'Sign out everywhere?',
    signOutEverywhereBody:
      'Every active session for {{name}} will be ended. If this is your own account, the device you are currently using is kept.',
    reason: 'Reason',
    reasonHint:
      'Required and recorded in the audit log. Enter 5 to 200 characters.',
    reasonInvalid: 'Enter a reason between 5 and 200 characters.',
    revokeFailed: 'The device could not be signed out. Try again.',
    signedOutToast: 'Device signed out',
    staffSignedOutToast: '{{count}} session(s) signed out',
    errors: {
      currentSession:
        'This is the device you are currently using. Use the main Sign out button instead.',
      sessionNotFound:
        'That session no longer exists. Close this message and refresh the list.',
      staffNotFound:
        'This staff member is no longer available in your branch scope.',
      superAdminOnly: 'Only a Super Admin can manage a Super Admin’s sessions.',
      invalidReason: 'Enter a reason between 5 and 200 characters.',
      forbidden: 'Your active role cannot manage this staff member’s sessions.',
    },
  },
  twoFactor: {
    getNewCodes: 'Get new recovery codes',
    appCodeForNewCodes: 'Code from your app, to get new recovery codes',
    newCodesHint: 'This immediately invalidates every previous recovery code.',
    savedCodes: 'I’ve saved these codes',
    oneTimeAcknowledgement:
      'These codes are shown only once. Copy or download them before continuing.',
    invalidCode:
      'That code was not accepted. Enter the current 6-digit code from your authenticator app.',
    notEnabled:
      'Two-factor is no longer enabled for this account. Close this dialog and try again.',
    rateLimited:
      'Too many attempts. Wait a minute before requesting new recovery codes again.',
    genericError: 'New recovery codes were not created. Try again.',
  },
  locum: {
    managementTitle: 'Locums',
    myContract: 'My contract',
    myLocumPay: 'My locum pay',
    contractDetails: 'Contract details',
    contractStatus: 'Status',
    contractPeriod: 'Contract period',
    locum: 'Locum',
    fullName: 'Full name',
    email: 'Email',
    branch: 'Branch',
    chooseBranch: 'Choose a branch',
    startsOn: 'Starts on',
    endsOn: 'Ends on',
    hourlyRate: 'Hourly rate (RM)',
    perSessionRate: 'Per-session rate (RM)',
    rateBasis: 'Rate basis',
    rate: 'Rate (RM)',
    chooseLocum: 'Choose the locum',
    noLocumPeople: 'No locums to give a contract to',
    noLocumPeopleBody:
      'A Super admin invites a locum in Staff & roles first; then the contract is added here.',
    perSessionNowHourly:
      'This contract was paid per session. Contracts are hourly now: saving makes it hourly at the rate below.',
    rateBases: {
      hourly: 'Hourly',
      per_session: 'Per session',
    },
    notes: 'Contract notes',
    accepted: 'Accepted',
    notAccepted: 'Not accepted yet',
    invite: 'Invite locum',
    sendInvitation: 'Send invitation',
    invited: 'Locum invitation created',
    viewContract: 'View contract',
    endContract: 'End contract',
    contractEnded: 'Locum contract ended',
    noLocums: 'No locum contracts',
    noLocumsBody: 'Invite a locum when temporary cover is needed.',
    noContract: 'No locum contract',
    noContractBody:
      'Your clinic manager will provide contract details before your first shift.',
    errors: {
      notFoundOwn:
        'No contract is linked to your account. Ask the clinic manager to add it to your staff record.',
      notFoundManage:
        'This contract is no longer available. Refresh the staff record before making another change.',
      invalidRate: 'Enter an hourly rate greater than RM 0.',
    },
    status: {
      invited: 'Invited',
      upcoming: 'Upcoming',
      active: 'Active',
      ended: 'Ended',
      cancelled: 'Cancelled',
    },
  },
  panelLimits: {
    ledgerTitle: 'Patient panel usage',
    contractLimits: 'Contract limits',
    remainingByPatient: 'Patient remaining limits',
    panel: 'Panel',
    patient: 'Patient',
    claim: 'Claim',
    claimUsage: 'Claim usage',
    annualUsage: 'Annual usage',
    remaining: 'Remaining',
    status: 'Status',
    consultationCap: 'Consultation cap',
    testsCap: 'Tests cap',
    proceduresCap: 'Procedures cap',
    annualPatientLimit: 'Annual patient limit',
    warningThreshold: 'Warning threshold',
    actions: 'Actions',
    consultationCapRm: 'Consultation cap per visit (RM)',
    testsCapRm: 'Tests cap per visit (RM)',
    proceduresCapRm: 'Procedures cap per visit (RM)',
    annualPatientLimitRm: 'Annual patient limit (RM)',
    warningThresholdPercent: 'Warning threshold (%)',
    warningHint: 'Show a warning when usage reaches this percentage.',
    blankForNoCap: 'Leave blank when this panel has no cap.',
    invalid:
      'Enter valid amounts and a whole-number warning threshold from 1 to 100.',
    saved: 'Panel limits saved',
    noConfigurations: 'No panel limits configured',
    noConfigurationsBody:
      'Limits appear here after a panel contract has limit rules.',
    noUsage: 'No panel usage recorded',
    noUsageBody: 'Claim usage appears here when panel claims are recorded.',
    configurationNote:
      'Limit warnings support claim review. They do not approve or reject a claim automatically.',
    ofLimit: 'of {{limit}}',
    states: {
      ok: 'Within limit',
      near: 'At or above {{threshold}}%',
      over: 'Limit exceeded',
    },
    errors: {
      invalidLimits:
        'The panel limits were not saved. Check each cap and the warning threshold.',
      invalidEvaluation:
        'The panel limit check could not run. Check the patient, panel and draft bill lines.',
      notFound: 'Panel limits could not be found for this contract.',
    },
  },
  panelHistory: {
    byPatient: 'By patient',
    searchLabel: 'Patient name or IC',
    searchHint: 'Enter at least 2 characters. IC results remain masked.',
    searchPlaceholder: 'Search patient name or IC',
    search: 'Search',
    searchResults: 'Patient search results',
    searchError: {
      invalid: 'Check the patient search',
      forbidden: 'Patient search is not available for this role',
      failed: 'Patient search failed',
    },
    noPatients: 'No matching patients',
    noPatientsBody: 'Check the name or IC and try again.',
    patientLedgerTitle: 'Panel & Claims · {{name}}',
    patient: 'Patient',
    panel: 'Panel',
    panels: 'Panels',
    claims: 'Claims',
    claim: 'Claim',
    claimed: 'Claimed',
    approved: 'Approved',
    patientShare: 'Patient share',
    totalClaimed: 'Total claimed',
    totalApproved: 'Total approved',
    remaining: 'Remaining',
    remainingLimits: 'Remaining panel limits',
    status: 'Status',
    actions: 'Actions',
    viewLedger: 'View ledger',
    exportClaims: 'Export claims history',
    exported: 'Claims history exported',
    exportedBody:
      '{{fileName}} contains {{count}} claims. The export was audited.',
    exportError: {
      forbidden: 'Claims history export is not available for this role',
      empty: 'There are no claims to export',
      failed: 'Claims history export failed',
    },
    noCap: 'No annual cap',
    usedNoCap: '{{used}} used',
    usedOfLimit: '{{used}} of {{limit}} used · {{period}}',
    capWarning: 'Usage is at or above the {{threshold}}% warning level.',
    capExceeded: 'The recorded usage has reached or exceeded this cap.',
    noBalances: 'No panel limit balances',
    noBalancesBody: 'Balances appear when this patient has panel coverage.',
    activeOverrides: 'Active overrides',
    noOverrides: 'No active overrides',
    noOverridesBody:
      'Active panel approvals and guarantee letters appear here.',
    override: 'Override',
    approvedCoverage: 'Approved coverage',
    validity: 'Validity',
    until: 'Until {{date}}',
    noExpiry: 'No expiry recorded',
    recordedBy: 'Recorded by',
    pastClaims: 'Past panel claims',
    noClaims: 'No panel claims',
    noClaimsBody: 'Past panel claims appear here after they are recorded.',
    balanceStatus: {
      within_limit: 'Within limit',
      warning: 'Near cap',
      exceeded: 'Cap reached',
    },
    overrideTypes: {
      panel_approval: 'Panel approval',
      guarantee_letter: 'Guarantee letter (GL)',
    },
    claimStatus: {
      draft: 'Draft',
      submitted: 'Submitted',
      queried: 'Queried',
      approved: 'Approved',
      paid: 'Paid',
      rejected: 'Rejected',
      cancelled: 'Cancelled',
    },
  },
  panelReceivables: {
    title: 'Panel receivables',
    intro:
      'Track each panel batch from checking and submission through matching and closure.',
    workflow: 'Panel receivables workflow',
    view: 'Receivables view',
    board: 'Board',
    list: 'List',
    batch: 'Batch',
    batchDetails: 'Batch details',
    batchTitle: 'Batch {{reference}}',
    panel: 'Panel',
    period: 'Period',
    state: 'State',
    patients: 'Patients',
    patient: 'Patient',
    outstanding: 'Outstanding',
    ageingLabel: 'Ageing',
    actionsLabel: 'Actions',
    viewBatch: 'View batch',
    patientClaims: 'Patient claims',
    visitDate: 'Visit date',
    claimed: 'Claimed',
    approved: 'Approved',
    received: 'Received',
    pending: 'Pending',
    remittanceTotal: 'Remittance total',
    allocatedTotal: 'Allocated total',
    outstandingTotal: 'Outstanding total',
    variance: 'Variance',
    remittanceMatching: 'Remittance matching',
    allocationAmount: 'Allocated {{amount}}',
    confirmKnockOff: 'Confirm knock-off',
    varianceBlocksClosure:
      'Resolve the variance before this batch can be closed.',
    allocationUpdated: 'Allocation decision saved',
    knockedOff: 'Remittance knocked off',
    sendBatch: 'Send batch',
    batchSent: 'Batch sent',
    runAutoMatch: 'Run auto-match',
    autoMatched: 'Remittance matching refreshed',
    followUpLog: 'Follow-up activity',
    logFollowUp: 'Log follow-up',
    saveFollowUp: 'Save follow-up',
    followUpDate: 'Follow-up date',
    channel: 'Channel',
    nextFollowUp: 'Next follow-up date',
    dateRequired: 'Choose a follow-up date.',
    followUpNote: 'Follow-up note',
    notePlaceholder: 'Record contact, response, or next action',
    noteRequired: 'Enter a follow-up note.',
    addNote: 'Add note',
    noteAdded: 'Follow-up note added',
    channels: {
      email: 'Email',
      phone: 'Phone',
      portal: 'Panel portal',
      other: 'Other',
    },
    noFollowUps: 'No follow-up activity',
    noFollowUpsBody: 'Notes appear here after Finance records a follow-up.',
    noClaims: 'No patient claims in this batch',
    noClaimsBody: 'Patient claims appear after they are added to the batch.',
    noBatches: 'No panel receivable batches',
    noBatchesBody: 'Batches appear here when panel claims are ready to check.',
    noBatchesInState: 'No batches in this state',
    movedTo: 'Batch moved to {{state}}',
    daysOutstanding_one: '{{count}} day',
    daysOutstanding_other: '{{count}} days',
    states: {
      check: 'Check',
      send: 'Send',
      waiting: 'Waiting',
      match: 'Match',
      closed: 'Closed',
    },
    actions: {
      send: 'Ready to send',
      waiting: 'Mark sent',
      match: 'Match payment',
      closed: 'Close batch',
    },
    ageing: {
      '0_30': '0–30 days',
      '31_60': '31–60 days',
      '61_90': '61–90 days',
      '90_plus': '90+ days',
    },
  },
  onlinePayments: {
    title: 'Online payments',
    queueFilter: 'Online payment queue',
    allPayments: 'All payments',
    needsReview: 'Needs review',
    allStatuses: 'All statuses',
    applyFilters: 'Apply filters',
    clearFilters: 'Clear',
    created: 'Created',
    payment: 'Payment',
    bill: 'Bill',
    patient: 'Patient',
    amount: 'Amount',
    status: 'Status',
    actions: 'Actions',
    billId: 'Bill ID',
    billIdPlaceholder: 'Exact bill UUID',
    from: 'From',
    to: 'To',
    billSummary: '{{status}} · {{balance}} due',
    billMethod: 'Online payment ({{provider}}) · {{method}}',
    providerReference: 'Provider payment ID',
    reviewReason: 'Why it needs review',
    resolution: 'Resolution',
    resolve: 'Resolve',
    resolvePermissionRequired: 'Finance resolver required',
    noActionRequired: 'No action required',
    resolveTitle: 'Resolve payment for {{billNumber}}',
    confirmResolution: 'Confirm resolution',
    cancel: 'Cancel',
    refundNotice:
      'Refund the payment in the Stripe dashboard first. This action only records the completed refund; it does not move money.',
    refundReference: 'Stripe refund ID',
    refundReferencePlaceholder: 're_…',
    refundReferenceHint:
      'Enter the completed Stripe refund ID beginning with re_.',
    refundReferenceInvalid:
      'Enter a valid Stripe refund ID beginning with re_.',
    note: 'Resolution note',
    noteHint: '{{count}} of 500 characters',
    noteTooLong: 'Keep the note to 500 characters.',
    noPayments: 'No online payments',
    noPaymentsBody:
      'Online payments appear here after patients pay in the app.',
    noReviewPayments: 'No payments need review',
    noReviewPaymentsBody:
      'Payments appear here when money was taken but could not be applied to the bill.',
    methods: {
      card: 'card',
      grabPay: 'GrabPay',
      unknown: 'Method pending',
    },
    providers: {
      stripe: 'Stripe',
    },
    billStatuses: {
      open: 'Open',
      closed: 'Closed',
      void: 'Void',
    },
    statuses: {
      pending: 'Pending',
      succeeded: 'Succeeded',
      failed: 'Failed',
      cancelled: 'Cancelled',
      needs_review: 'Needs review',
    },
    resolutions: {
      refunded: 'Refunded',
      applied: 'Applied to bill',
    },
    resolvedToast: {
      refunded: 'Refund recorded',
      applied: 'Payment applied to the bill',
    },
    reviewReasons: {
      amount_mismatch: 'Provider amount did not match',
      amount_changed: 'Bill amount changed',
      not_open: 'Bill is no longer open',
      nothing_due: 'Nothing is due on the bill',
      pending_approval: 'Bill has a pending approval',
      counter_needed: 'The bill needs counter review',
    },
  },
  limitBilling: {
    bannerTitle: 'Panel limit assessment',
    assessing: 'Checking the current panel limits and patient shareâ€¦',
    assessmentFailed:
      'The panel limit assessment could not be loaded. Payment is blocked until it is available.',
    confirmPatient:
      'I confirmed the panel coverage and patient-pay amount with the patient.',
    enterPanelApproval: 'Enter panel approval',
    perVisitCapExceeded: 'Per-visit panel cap exceeded',
    perVisitCapExceededBody:
      '{{eligible}} is eligible; the visit cap is {{cap}}.',
    lookupTitle: 'Limit billing',
    billId: 'Bill ID',
    billIdHint: 'Enter the bill identifier from the billing flow.',
    checkLimits: 'Check limits',
    noBill: 'No bill selected',
    noBillBody: 'Look up a bill to check panel limits and the patient share.',
    panel: 'Panel',
    assessmentStatus: 'Limit status',
    billTotal: 'Bill total',
    nearLimitTitle: 'Confirm near-limit billing',
    nearLimitBody:
      'This bill is close to a configured panel limit. Check the split with the patient before continuing.',
    confirmContinue: 'Confirm and continue',
    confirmAuditNote:
      'Your confirmation, staff identity and time will be recorded in the billing audit trail.',
    splitTitle: 'Panel and patient split',
    splitBody:
      'Coverage is limited to the eligible amount and the remaining visit and annual limits. Any balance becomes patient co-pay or out-of-pocket.',
    eligibleTotal: 'Eligible total',
    panelPays: 'Panel pays',
    patientPays: 'Patient pays',
    patientPayNotice:
      'Tell the patient the amount due before recording payment.',
    overrideTitle: 'Panel approval / GL override',
    overrideBody:
      'Use only when the panel has approved additional coverage. The reference, reason, staff identity and time are audited.',
    overrideType: 'Override type',
    panelApproval: 'Panel approval',
    guaranteeLetter: 'Guarantee letter (GL)',
    reference: 'Approval / GL reference',
    referenceRequired: 'Enter the panel approval or GL reference.',
    approvedCoverage: 'Approved panel coverage (RM)',
    coverageInvalid: 'Enter coverage no greater than the bill total.',
    reason: 'Reason',
    reasonHint: 'Explain why the configured limit does not apply.',
    reasonRequired: 'Enter a reason for the override.',
    applyOverride: 'Apply override',
    overrideApplied: 'Limit override applied',
    auditTitle: 'Billing control audit',
    noAudit: 'No billing controls recorded',
    noAuditBody:
      'Confirmations, applied splits and overrides will appear here.',
    status: {
      within_limit: 'Within limit',
      near_limit: 'Near limit',
      over_limit: 'Over limit',
    },
    auditActions: {
      near_limit_confirmed: 'Near-limit confirmation',
      split_applied: 'Patient-pay split applied',
      override_applied: 'Panel approval / GL override applied',
    },
  },
  locumPay: {
    history: 'History',
    myTitle: 'My Locum Pay',
    managerTitle: 'Locum pay review',
    financeTitle: 'Locum payouts',
    locum: 'Locum',
    week: 'Week',
    attendance: 'Attendance',
    rate: 'Hourly rate',
    grossPay: 'Gross pay',
    statusLabel: 'Status',
    actions: 'Actions',
    approve: 'Approve',
    approved: 'Locum pay approved',
    correctionCount_one: '{{count}} attendance correction required',
    correctionCount_other: '{{count}} attendance corrections required',
    correctAttendance: 'Correct attendance {{count}}',
    awaitingAttendanceCorrection: 'Awaiting attendance correction',
    progress: 'Payout progress',
    weeklyProgress: 'Weekly payout progress',
    noReview: 'No locum pay to review',
    noReviewBody: 'Weekly pay appears after locum attendance is recorded.',
    noOwnPay: 'No locum pay yet',
    noOwnPayBody: 'Your weekly pay appears after attendance is recorded.',
    noPayouts: 'No approved locum payouts',
    noPayoutsBody: 'Manager-approved weekly pay appears here for payment.',
    bankReference: 'Bank reference',
    bankReferenceHint: 'Record the reference returned by the bank transfer.',
    bankReferenceRequired: 'Enter the bank reference.',
    processPayment: 'Process payment',
    paymentTitle: 'Pay {{name}}',
    confirmPayment: 'Confirm payment',
    paid: 'Locum payment recorded',
    exportBooks: 'Export Books payroll feed',
    exported: 'Payroll feed exported',
    exportedBody: '{{fileName}} · {{count}} rows',
    status: {
      running: 'Running',
      submitted: 'Submitted',
      approved: 'Approved',
      paid: 'Paid',
    },
    errors: {
      attendanceCorrectionRequired:
        'Resolve the linked attendance correction before approving this pay.',
      notApproved: 'This weekly pay must be approved before it can be paid.',
      bankReferenceRequired:
        'Enter the bank reference before recording payment.',
      notFound:
        'This locum pay record could not be found. Refresh and try again.',
    },
  },
  revenue: {
    myCalendar: 'My revenue calendar',
    doctorCalendar: 'Doctor revenue calendar',
    doctorRevenue: 'Doctor revenue',
    period: 'Revenue period',
    periodSummary: 'Revenue period summary',
    month: 'Month',
    doctor: 'Doctor',
    chooseDoctor: 'Choose a doctor',
    visits: 'Visits',
    visit: 'Visit',
    visitCount_one: '{{count}} visit',
    visitCount_other: '{{count}} visits',
    gross: 'Gross revenue',
    panel: 'Panel',
    selfPay: 'Self-pay',
    doctorShare: 'Doctor share',
    // RL-1862 (gap #139): a RM 0.00 share means no pay basis is set yet.
    noPayBasis:
      'Doctor share is RM 0.00. If that is not right, check the pay basis in Books (Books & payouts › Doctor fees & payroll).',
    noPayBasisAsk:
      'Doctor share is RM 0.00. If that is not right, ask Finance to check the pay basis.',
    setPayBasis: 'Check the pay basis in Books',
    booksBasis:
      'Doctor share uses the recorded Books pay basis. Figures include closed bills only.',
    export: 'Export CSV',
    loading: 'Loading revenue',
    loadingDay: 'Loading day detail',
    dayDetail: 'Day detail',
    previousDay: 'Previous day',
    nextDay: 'Next day',
    calendarDayLabel: '{{date}}, {{visits}} visits',
    noFigures: 'No figures',
    notOnRoster: 'Not on the roster that day',
    notOnRosterBody:
      'There is no scheduled doctor shift for this date, so no revenue detail is shown.',
    noClosedVisits: 'No closed visits',
    noClosedVisitsBody:
      'This rostered day has no visits with a closed bill yet.',
    visitsForDay: 'Revenue visits for {{date}}',
    patient: 'Patient',
    closedAt: 'Bill closed',
    payer: 'Payer',
    payerType: {
      panel: 'Panel',
      self_pay: 'Self-pay',
    },
    noDoctors: 'No doctors in this scope',
    noDoctorsBody:
      'Doctors appear here when they are assigned to a branch in your current scope.',
    errors: {
      forbidden: 'You do not have access to revenue outside your scope.',
      doctorNotFound: 'This doctor is not available in your current scope.',
      invalidPeriod: 'Choose a valid revenue period and try again.',
      exportFailed: 'Revenue export failed',
    },
  },
  leaflets: {
    title: 'Medicine leaflets',
    searchLabel: 'Medicine',
    searchPlaceholder: 'Name or generic name',
    stateFilter: 'Leaflet state',
    states: {
      all: 'All',
      approved: 'Approved',
      draft: 'Draft waiting',
      none: 'No leaflet',
    },
    applyFilters: 'Apply filters',
    clearFilters: 'Clear',
    medicine: 'Medicine',
    state: 'State',
    lastChange: 'Last change',
    actions: 'Actions',
    open: 'Open',
    approvedBadge: 'Approved v{{version}}',
    draftBadge: 'Draft v{{version}}',
    noLeaflet: 'No leaflet',
    noMedicines: 'No medicines match',
    noMedicinesBody: 'Try another name, or clear the state filter.',
    changeMeta: '{{when}} · {{who}}',
    languages: { en: 'English', ms: 'Malay' },
    fields: {
      howToTake: 'How to take it',
      sideEffects: 'Side effects',
      whenToCallClinic: 'When to call the clinic',
    },
    fieldLabel: '{{field}} ({{language}})',
    charCount: '{{count}} / {{max}} characters',
    fieldTooLong: 'Use at most {{max}} characters.',
    genericName: 'Generic name',
    qrAddress: 'Label QR address',
    qrNone: 'Shown once a leaflet is approved.',
    published: 'Approved leaflet',
    publishedNone:
      'No approved leaflet. Nothing is served to the app or the QR page.',
    publishedMeta: 'Version {{version}} · approved {{when}} by {{who}}',
    draftSection: 'Draft',
    draftNone: 'No draft.',
    draftMeta: 'Version {{version}} · last edited {{when}} by {{who}}',
    startDraft: 'Start a draft',
    startNewVersion: 'Start a new version',
    startDraftHint:
      'A new version starts from the approved text. The approved leaflet keeps being served until the new one is approved.',
    saveDraft: 'Save draft',
    approve: 'Approve',
    retireLeaflet: 'Retire leaflet',
    discardDraft: 'Discard draft',
    savedToast: 'Draft saved',
    startedToast: 'Draft started',
    approvedToast: 'Leaflet approved',
    retiredToast: 'Leaflet retired',
    discardedToast: 'Draft discarded',
    approveHint:
      'A second person must approve a leaflet: whoever wrote or last edited the draft cannot approve it.',
    saveFirst: 'Save your changes before approving.',
    fillAll: 'Fill in every field in English and Malay to approve.',
    readOnlyNotice: 'You can read leaflets here but not edit them.',
    selfApprovalTitle: 'A second person must approve this leaflet',
    selfApprovalBody:
      'You wrote or last edited this draft. Ask another pharmacist or manager to approve it.',
    retireApprovedTitle: 'Retire this leaflet?',
    retireApprovedBody:
      'The label QR page and the app answer "not found" until a new version is approved.',
    discardTitle: 'Discard this draft?',
    discardBody:
      'The draft is kept for the record but can no longer be edited or approved.',
    confirmRetire: 'Yes, retire it',
    confirmDiscard: 'Yes, discard it',
    keep: 'Keep',
    history: 'Version history',
    historyEmpty: 'No versions yet.',
    historyUpdating: 'Updating the history…',
    historyRow: 'Version {{version}} · {{status}}',
    versionStatuses: {
      draft: 'Draft',
      approved: 'Approved',
      retired: 'Retired',
    },
    historyEdited: 'Edited {{when}} by {{who}}',
    historyApproved: 'Approved {{when}} by {{who}}',
    loadingDetail: 'Loading leaflet',
  },
  dispensingLabels: {
    title: 'Medicine labels',
    dialogTitle: 'Labels for {{number}}',
    open: 'Labels',
    previewTitle: 'Label preview',
    previewLoading: 'Loading the label preview',
    previewIntro:
      'This is a preview only. Looking at it records nothing; only Print labels records a print request.',
    printIntro:
      'Print labels records one print request for this prescription in the audit log, then opens the print dialog. The browser cannot confirm that paper came out.',
    reprintIntro:
      'Labels were already printed for this prescription ({{count}}). A reprint needs a reason and is logged.',
    reasonLabel: 'Reason for the reprint',
    reasonHint: '{{count}} / {{max}} characters',
    reasonRequiredByApi:
      'The server needs a reason for this reprint. Enter one, then press Print labels again.',
    close: 'Close',
    medicineLabels: 'Medicine labels ({{count}})',
    bagLabel: 'Bag label',
    bagItems: 'In this bag',
    print: 'Print labels',
    printed:
      'Print requested. To print again, close this and reopen the labels: a reason is required.',
    printBlocked:
      'Preview only outside production: nothing is recorded or printed from here, and the Print button is switched off.',
    printLimits:
      'This cannot stop a browser-level print or a screenshot. The preview is hidden from the printed page, but it is not a technical lock.',
    nonProductionBand: 'TEST / STAGING · NOT FOR PATIENT USE',
    scanForLeaflet: 'Scan for leaflet',
    scanForLeafletMs: 'Imbas untuk risalah',
    noLeafletQr:
      'No leaflet QR: no approved leaflet for this medicine, or the leaflet QR address is not set on this server.',
    // RL-1644: the one-line notice outside production.
    stagingNotice:
      'Staging preview: nothing prints, and this prescription cannot record a TEST print.',
    stagingNoticeTest:
      'Staging preview: nothing prints. Record TEST print counts one print so it can be dispensed.',
    controlled: 'Ubat Terkawal / Controlled Medicine',
    externalUse: 'NOT TO BE TAKEN · FOR EXTERNAL USE ONLY',
    externalUseLanguages:
      'The external-use warning is shown in English only. The red Malay, Chinese and Tamil wording that the regulations ask for is not set up yet and needs a clinic decision.',
    recordTest: 'Record TEST print (nothing is printed)',
    testRecordIntro:
      'This is a dedicated QA prescription: Record TEST print counts one print request in the audit log (as a TEST print) so it can be dispensed. No paper is printed.',
    testRecordNotOffered:
      'No print can be recorded for this prescription from here, so it cannot be dispensed on staging. Only dedicated QA prescriptions can record a TEST print.',
    testRecorded: 'TEST print recorded. Nothing was printed.',
    testRecordedNote:
      'TEST print recorded (audited). To record another, close this and reopen the labels: a reason is required.',
    externalUseTestPlaceholders:
      'The Malay, Chinese and Tamil external-use wording is not approved yet. The dashed red lines are TEST placeholders, not wording: they appear only outside production and are never printed.',
    externalUsePlaceholder:
      '[TEST PLACEHOLDER · Malay / Chinese / Tamil external-use wording not approved]',
    layoutLabel: 'Label size',
    layoutHint:
      'TEST default 70 × 40 mm. Text that does not fit continues on a second label ("2/2"); nothing is cut off. The final size is decided with the printer (production readiness).',
    layoutOption: '{{width}} × {{height}} mm',
    dosePictogram: 'Dose times',
    doseTimes: {
      morning: 'Morn',
      noon: 'Noon',
      evening: 'Eve',
      night: 'Night',
    },
    rxLine: 'Rx {{number}} · {{date}}',
    doctorLine: 'Dr {{name}}',
    batchLine: 'Batch {{batch}}',
    qrLoading: 'Preparing the QR codes',
    qrFailed: 'The leaflet QR could not be drawn. Do not print this label.',
    labelOverflow:
      '{{count}} label(s) still have more text than fits on the label, so part of it would be cut off. Do not print them: choose a larger label size.',
  },
  common: {
    loading: 'Loading…',
    retry: 'Try again',
    cancel: 'Cancel',
    close: 'Close',
    save: 'Save',
    saved: 'Saved',
    notRecorded: 'Not recorded',
    notAvailable: 'Not available',
    requestId: 'Reference: {{id}}',
    problemCode: 'Code: {{code}}',
    comingSoon: 'Not built yet',
  },
  consultation: {
    title: 'Consultation',
    history: {
      title: 'Previous visits',
      show: 'Show previous visits',
      hide: 'Hide previous visits',
      loading: 'Loading previous visits',
      empty: 'No earlier visit with a consultation is on this record.',
      allergies: 'Allergies',
      noAllergies: 'None recorded',
      diagnosis: 'Diagnosis',
      medicines: 'Medicines',
      none: 'None recorded',
      doctor: 'Doctor',
      open: 'Consultation still open',
      fullRecord: 'Open the full record',
    },
    loading: 'Loading consultation',
    backToPatients: 'Back to my patients',
    backToConsultation: 'Back to consultation',
    statusOpen: 'Open',
    statusClosed: 'Closed',
    checklistLabel: 'Consultation sign and close checklist',
    beforeClose: 'Complete before signing and closing',
    finishDraftOrders: 'Sign the prescription and send all draft orders.',
    closeEncounter: 'Close encounter',
    closed: 'Encounter closed',
    patientAdvice: 'Advice for the patient',
    patientAdviceHint:
      'The patient sees this in the app. Your notes above stay private.',
    patientAdviceNone: 'No advice was given to the patient.',
    labOrders: 'Lab orders',
    labOrdersSummary: 'Handoff to the lab desk',
    labOrdersLoading: 'Loading lab orders',
    labOrdersErrorTitle: 'Lab orders could not be loaded',
    labOrdersErrorBody:
      'The linked lab-order handoff is temporarily unavailable.',
    labOrdersEmptyTitle: 'No lab orders',
    labOrdersEmptyBody: 'No lab orders are linked to this consultation.',
    labOrderPendingNumber: 'Lab order',
    labOrderTests: 'Tests: {{tests}}',
    labOrderStatusDraft: 'Draft',
    labOrderStatusOrdered: 'Ordered',
    labOrderStatusCollected: 'Sample collected',
    labOrderStatusDispatched: 'Dispatched to lab',
    labOrderStatusResultsReady: 'Results ready',
    labOrderStatusReviewed: 'Reviewed',
    labOrderStatusCancelled: 'Cancelled',
  },
  timeSlip: {
    title: 'SLIP MASA · Time slip',
    print: 'Print',
    printLabel: 'Print time slip {{number}}',
    patient: 'Patient',
    for: 'Slip for',
    forPatient: 'The patient',
    forCompanion: 'Companion: {{name}}',
    date: 'Date',
    arrived: 'Arrived',
    leaving: 'Leaving',
    issuedBy: 'Issued by',
    attendanceOnly:
      'This slip confirms attendance at the clinic only. It is not a medical certificate and does not grant sick leave.',
    qrAlt: 'QR code to verify this time slip',
    scanToVerify: 'Scan to verify this slip',
  },
  referral: {
    title: 'Referral letter',
    loading: 'Loading referral letter',
    notFound: 'Consultation not found',
    back: 'Back to consultation',
    statusDraft: 'Draft',
    statusIssued: 'Issued',
    saveDraft: 'Save draft',
    signAndIssue: 'Sign & issue',
    print: 'Print',
    draftSaved: 'Draft saved',
    draftSavedBody:
      'Saved with this consultation: it opens on any computer until the letter is signed.',
    draftNotSaved: 'Draft not saved',
    autosaved: 'Draft saved {{time}}',
    autosaveFailed: 'Draft not saved. Use Save draft to try again.',
    loadingDraft: 'Loading the saved draft',
    issued: 'Referral signed and issued',
    notIssued: 'Referral not issued',
    noneIssued: 'No referral letter issued',
    noneIssuedBody:
      'This consultation does not have a referral letter available to read.',
    form: 'Referral letter form',
    formKicker: 'Referral details',
    details: 'Prepare the letter',
    readOnly: 'Read only',
    referTo: 'Refer to',
    chooseSpecialty: 'Choose a specialty',
    hospital: 'Hospital or specialist',
    hospitalExample: 'e.g. Hospital Melaka',
    urgencyLabel: 'Urgency',
    urgency: {
      routine: 'Routine',
      within_2_weeks: 'Within 2 weeks',
      urgent_today: 'Urgent: today',
    },
    reason: 'Reason for referral',
    reasonHint:
      'Use clinically relevant details only. Medicines, allergies and recent vitals are added from the record.',
    reasonPlaceholder:
      'Describe the reason for referral and clinical question.',
    recordNote:
      'The issued letter is kept with this visit and carries the doctor’s digital signature and a QR check.',
    preview: 'Referral letter preview',
    livePreview: 'Live preview',
    a4Preview: 'A4 letter',
    previewHint: 'Updates as you type',
    clinic: 'Ommri Care clinic',
    date: 'Date',
    reference: 'Reference',
    documentKicker: 'Clinical correspondence',
    documentTitle: 'Referral letter',
    salutation: 'Dear Doctor,',
    reLine: 'Re:',
    dob: 'DOB {{date}}',
    introduction:
      'I am referring this patient to {{specialty}} at {{hospital}} for your assessment and management.',
    specialistPlaceholder: 'the selected specialty',
    hospitalPlaceholder: 'the receiving facility',
    clinicalSummary: 'Relevant clinical information',
    allergies: 'Allergies',
    medicines: 'Current medicines',
    vitals: 'Recent vitals',
    signedBy: 'Digitally signed by',
    mmc: 'MMC {{number}}',
    mmcNotRecorded: 'MMC number not recorded',
    qrAlt: 'Referral verification QR code',
    scanToVerify: 'Scan to verify this referral',
  },
  queue: {
    roomOccupied: 'Room already occupied',
    notDone: 'Not done',
    // RL-1660: one "Verify and check in" for an unverified patient.
    verifyCheckIn: {
      action: 'Verify and check in',
      title: 'Verify and check in',
      details: 'Details on the record',
      checklist: 'What to check',
      checkFirst: 'Check with the MyKad in your hand',
      card: 'The MyKad is the patient’s (name and IC match the record)',
      face: 'The face matches the MyKad photo',
      detailsRight: 'The name, IC and date of birth above are right',
      save: 'All match: verify and check in',
      checkInOnly: 'Check in',
      booking: 'Booking',
      bookedFor: 'Booked today {{time}} · {{service}}',
      service: 'Service (walk-in)',
      done: '{{name}}: verified · ticket {{number}}',
      late: 'More than 15 minutes late: queued with walk-in priority. Tell the patient now.',
      unlinked: 'The app account was unlinked from this record and signed out.',
      askAppAccount:
        'This record is linked to an app account. Ask the patient whether it is theirs, then save again.',
      provider:
        'The eKYC provider is not connected yet: your check stands for its result. Once it is connected, its pass or fail shows here.',
    },
    // RL-1664: Check cover for a membership the patient typed in the app.
    cardCheck: {
      tick: 'Card checked: it matches what the patient entered',
      memberNo: 'Member / GL number on the card (if different)',
      memberNoShort: 'Enter at least 2 characters, or leave it empty.',
      hint: 'Saving marks the membership as checked: {{result}}.',
      hintPayer:
        'Saving marks the membership as checked and makes it the payer for this visit: {{result}}.',
      resultCovered: 'covered',
      resultNotCovered: 'still not covered (see above)',
      notAllowed:
        "Your role can't mark a membership as checked. Ask the clinic manager.",
      covered: 'Card checked · {{payer}} covers this visit',
      panel: 'panel',
      notCovered: 'Card checked · not covered',
      markedBody: 'The membership is marked as checked on the record.',
    },
    undo: 'Undo',
    // RL-1658: Cancel ticket and Done on the queue card.
    cancel: {
      action: 'Cancel ticket',
      title: 'Cancel ticket {{number}}',
      why: 'Why is {{name}} leaving the queue?',
      reason: {
        left: 'Left before being seen',
        mistake: 'Came by mistake',
        other: 'Other',
      },
      note: 'What happened',
      hint: 'Everyone behind moves up at once. If the patient has the app, it tells them their ticket was cancelled (not the reason).',
      keep: 'Keep ticket',
      confirm: 'Cancel ticket',
      toast: 'Ticket {{number}} cancelled',
      toastBody: 'You can undo this for 10 seconds.',
    },
    done: {
      action: 'Done',
      hint: 'Finish a visit with no consultation (vaccination only, collecting documents)',
      toast: 'Ticket {{number}} done',
      toastBody: 'You can undo this for 10 seconds.',
    },
  },
  myPatients: {
    filters: 'My patients filters',
    status: 'Status',
    service: 'Service',
    payer: 'Payer',
    search: 'Search',
    allStatuses: 'All statuses',
    statusOption: 'Status · {{name}}',
    allServices: 'All services',
    allPayers: 'All payers',
    serviceOption: 'Service · {{name}}',
    payerOption: 'Payer · {{name}}',
    selfPay: 'Self',
    searchPlaceholder: 'Patient name or ID',
    noMatches: 'No matching patients',
    noMatchesBody: "Change or clear the filters to see today's patients.",
    finishActiveFirst:
      'Close encounter {{number}} before opening another patient.',
    activePatientExists: 'Another patient is already active',
    continueEncounter: 'Continue encounter',
  },
  auth: {
    refreshingAccess: 'Checking your access',
    refreshFailed: 'Could not check your access',
    forgotPassword: 'Forgot password?',
    signInTitle: 'Sign in',
    signInSubtitle:
      'Use your staff email. Two-factor is required for protected roles.',
    email: 'Email',
    password: 'Password',
    signIn: 'Sign in',
    signOut: 'Sign out',
    chooseRoleTitle: 'Welcome, {{name}}',
    chooseRoleSubtitle:
      'Your account has more than one role. Choose the one you are working as.',
    signInAs: 'Sign in as',
    signInAsDemo: 'Sign in as (demo)',
    allBranches: 'All branches',
    sessionEnded: 'Your session ended. Sign in again.',
    rolesNote: 'Roles are assigned by the Super admin.',
    serverUnreachable:
      'Ommri Care cannot be reached right now. Check your connection and try again.',
  },
  shell: {
    search: 'Search patient, IC, claim…',
    notifications: 'Notifications',
    branch: 'Branch',
    allBranches: 'All',
    allBranchesOption: 'All branches',
    branchCurrent: 'Branch: {{name}}',
    findBranch: 'Find a branch',
    noBranchMatch: 'No branch matches',
    greetingMorning: 'Good morning, {{name}}',
    greetingAfternoon: 'Good afternoon, {{name}}',
    greetingEvening: 'Good evening, {{name}}',
    mainNav: 'Main navigation',
  },
  errors: {
    notFoundTitle: 'Page not found',
    notFoundBody: 'This address does not match any page in the admin.',
    forbiddenTitle: 'Not available for your role',
    forbiddenBody:
      'Your role does not include this page. Ask the Super admin if you need access.',
    // RL-1821: the Super admin grants access, so is told whose page it is instead.
    forbiddenPageFor: 'This page is for {{roles}}.',
    forbiddenNotInRole: 'This page is not part of your role.',
    pageOwners: {
      front_desk: 'the front desk',
      doctor: 'doctors',
      locum: 'locum doctors',
      mental_health: 'mental-health practitioners',
      nurse: 'nurses',
      lab: 'the lab',
      dispenser: 'the dispenser',
      finance: 'finance',
      clinic_manager: 'the clinic manager',
      super_admin: 'the Super admin',
    },
    noBranchTitle: 'No branch access',
    noBranchBody:
      'Your role is no longer assigned to any branch. Ask the Super admin to assign one, then sign in again.',
    branchSwitchedTitle: 'Branch access changed',
    branchSwitchedBody:
      'The branch you had selected is no longer one of yours. Now showing {{branch}}.',
    genericTitle: 'Something went wrong',
    genericBody: 'Try again. If it keeps happening, quote the reference below.',
    unknownCode: 'UNKNOWN_ERROR',
  },
  empty: {
    placeholderTitle: '{{page}} is not built yet',
    placeholderBody:
      'This page is part of the Ommri Care Admin v2 design and will be implemented in a later task.',
    dashboardValue:
      'Live figures appear once this area is connected to the API.',
  },
  books: {
    accounting: 'Accounting',
    invalidAmount: 'Enter an amount between RM 0 and RM 1,000,000.',
    invalidEpf: 'Enter a whole percentage between 0 and 30.',
    title: 'Books & payouts',
    companies: 'Companies',
    accounts: 'Company accounts',
    legal: 'Legal entities',
    fees: 'Doctor fees & payroll',
    exports: 'Accountant exports',
    einvoice: 'e-Invoice',
    employee: 'Employee',
    employeeNo: 'Employee number',
    allowance: 'Annual allowance',
    allowanceRM: 'Annual allowance (RM)',
    used: 'Used this year',
    left: 'Left',
    addEmployee: 'Add employee',
    invalidEmployee: 'Enter an employee name and number.',
    settlementTitle: 'Bank settlements against receipts',
    settlementBody:
      'Acquirer integration is not connected. Recorded simulated providers are labelled in the table; this page does not generate settlements with demo fee rates.',
    provider: 'From',
    receipts: 'Receipts',
    gross: 'Gross',
    fee: 'Fees',
    investigate: 'Investigate',
    note: 'Investigation note',
    reasonRequired: 'A reason is required.',
    accountingBody:
      'Preview figures come from recorded transactions. Posting stores the journal for the selected branch and day. No external accounting package or automatic posting schedule is connected.',
    postJournal: 'Post journal',
    postedDays: 'Posted days',
    einvoiceTitle: 'LHDN e-Invoice (MyInvois)',
    connected: 'Connected',
    disconnected: 'Not connected',
    enabled: 'On',
    disabled: 'Off',
    tin: 'Company TIN',
    configure: 'Configure',
    einvoiceBody:
      'Confirm applicability with the clinic before enabling. Saving this setting does not submit or validate an e-Invoice.',
    invalidTin: 'Enter a TIN between 5 and 20 characters to enable e-Invoice.',
    month: 'Month',
    payroll: 'Payroll export',
    work: 'Visits / sessions',
    epf: 'EPF deduction',
    editBasis: 'Edit fee basis',
    rateRM: 'Rate (RM)',
    epfPercent: 'EPF deduction (%) — leave blank to retain the recorded rate',
    employed: 'Employed',
    sessional: 'Sessional',
    locum: 'Locum',
    payrollBody:
      'Figures use the recorded fee basis and deduction. These are statements, not statutory payroll calculations. The payroll provider confirms EPF, SOCSO, EIS and PCB.',
    data: 'Data',
    export_sales: 'Sales',
    export_receipts: 'Receipts',
    export_claims: 'Claims',
    export_stock: 'Stock',
    buildCsv: 'Build CSV',
    settlements: 'Settlements',
    day: 'Date',
    method: 'Method',
    net: 'Net amount',
    journal: 'Journal',
    posted: 'Posted',
    preview: 'Preview',
    account: 'Account',
    debit: 'Debit',
    credit: 'Credit',
    doctorFees: 'Doctor fee basis',
    doctor: 'Doctor',
    basis: 'Basis',
    rate: 'Rate',
  },
  certificates: {
    window:
      'Up to 300 MCs in the selected branch scope, newest first. Search looks up the certificate number or patient name.',
    title: 'Medical certificates',
    serial: 'Serial',
    period: 'Unfit for duty',
    issued: 'Issued',
    scans: 'Scans',
    all: 'All',
    valid: 'Valid',
    revoked: 'Revoked',
    overlaps: 'Overlaps',
    summary: '{{valid}} valid · {{revoked}} revoked',
    search: 'Search certificates',
    empty: 'No matching certificates',
    days_one: '{{count}} day',
    days_other: '{{count}} days',
    view: 'View',
    viewLabel: 'View {{number}}',
    signals: 'Warning signs',
    reviewOnly: 'For review, not proof of wrongdoing',
    overlapPatient: 'Overlapping MCs · {{name}}',
    frequent: '{{count}} MCs in 30 days · {{name}}',
    notice:
      "Every MC carries a QR code. Scanning it shows whether the certificate is valid or revoked, with its dates and issuing clinic. MCs are issued only from a doctor's closed consultation.",
    docTitle: 'Medical certificate',
    patientName: 'Patient',
    identity: 'Identity document',
    noIdentity: 'No identity document recorded',
    duration: 'Duration',
    mmc: 'MMC {{no}}',
    noMmc: 'MMC number not recorded',
    mohNo: 'MOH reg. {{no}}',
    noDiagnosis: 'No diagnosis shown',
    addressMissing:
      'Clinic address not configured. Set it in Clinic setup › Branches.',
    addressMissingAtIssue: 'Clinic address not configured at issuance.',
    legacyAddress:
      "This certificate was issued before clinic addresses were saved with each MC. The address shown is the clinic's current setup, which may differ from the address when it was issued.",
    qrAlt: 'QR code to verify this MC',
    scanToVerify: 'Scan to verify',
    verifyLink: 'Verification link',
    copy: 'Copy',
    copied: 'Link copied',
    openCheck: 'Open the verification page',
    history: 'History',
    issuedBy: 'Issued by',
    lastScan: 'last {{when}}',
    revokedBy: 'Revoked by',
    revokedNote:
      'This certificate stays on record as issued. Scanning its QR now shows "Revoked".',
    print: 'Print MC',
    printRevoked: 'A revoked certificate cannot be printed.',
    printNeedsAddress:
      'Printing needs the clinic address. Set it in Clinic setup › Branches.',
    printNoIssueAddress:
      'This MC was issued without a clinic address, so it cannot be printed. Changing Clinic setup now does not change an issued MC.',
    pdf: 'Save as PDF',
    pdfHint: 'Opens the print dialog; choose "Save as PDF" as the destination.',
    printFailed: 'Not printed',
    printFailedBody:
      'The print request could not be recorded, so the certificate was not sent to print. Try again.',
    revoke: 'Revoke',
    revokeReason: 'Revoke (reason required)',
    revokeHint:
      'At least 5 characters. Kept with the record and in the audit log; the certificate itself is not changed.',
    revokePlaceholder: 'e.g. wrong dates; reissued as a new MC',
    revokedToast: 'Certificate revoked',
    revokeFailed: 'Not revoked',
  },
  verify: {
    pageTitle: 'Document check · Ommri Care',
    heading: 'Document check',
    valid: 'Valid {{doc}}',
    validBody: 'Issued by {{clinic}}.',
    revoked: 'Revoked',
    revokedOn: 'Revoked on {{date}}',
    revokedBody: 'This {{doc}} is no longer valid.',
    notConfirmed: 'This document cannot be confirmed as valid',
    notConfirmedBody: 'Call the issuing clinic to check it.',
    unknown: 'This QR code was not issued by Ommri Care',
    unknownBody:
      'The code does not match any document. Ask for the original document, or call the clinic.',
    unavailable: 'The check is not available right now',
    unavailableBody: 'Try again in a few minutes.',
    tooMany: 'Too many checks from this connection',
    tooManyBody: 'Wait a minute, then open the link again.',
    clinic: 'Issuing clinic',
    issued: 'Issued',
    footer:
      'This page shows only the document reference, whether it is valid, the issue date and the issuing clinic (and an MC’s dates): never patient details, medicines, vaccines or a diagnosis.',
    /** Title as printed (Bahasa Malaysia), English subtitle, noun for banners. */
    kinds: {
      medical_certificate: {
        ms: 'SIJIL CUTI SAKIT',
        en: 'Medical certificate',
        noun: 'medical certificate',
      },
      time_slip: { ms: 'SLIP MASA', en: 'Time slip', noun: 'time slip' },
      referral_letter: {
        ms: 'SURAT RUJUKAN',
        en: 'Referral letter',
        noun: 'referral letter',
      },
      imaging_referral: {
        ms: 'RUJUKAN PENGIMEJAN',
        en: 'Imaging referral (X-ray or scan)',
        noun: 'imaging referral',
      },
      vaccination_certificate: {
        ms: 'SIJIL VAKSINASI',
        en: 'Vaccination certificate',
        noun: 'vaccination certificate',
      },
      outside_prescription: {
        ms: 'PRESKRIPSI UNTUK FARMASI LUAR',
        en: 'Prescription for an outside pharmacy',
        noun: 'prescription',
      },
      other: { ms: 'DOKUMEN', en: 'Document', noun: 'document' },
    },
  },
  tax: {
    rate: 'Clinic-confirmed tax rate (%)',
    threshold: 'Clinic-confirmed registration threshold (RM)',
    scope: 'Calculation scope',
    scopeBody:
      'Clinic confirms: non-Malaysian patient-paid share only, all bill line kinds; panel share excluded.',
  },
  reports: {
    title: 'Reports',
    daily: 'Daily closing',
    revenue: 'Revenue',
    claims: 'Claims performance',
    stock: 'Stock',
    quality: 'Clinical quality',
    antibiotics: 'Antibiotics',
    byBranch: 'By branch',
    panelShare: 'Panel share',
    visitsToday: 'Visits today',
    afterRefunds: 'After refunds',
    cashDifference: 'Cash difference',
    noClosedShift: 'No closed shift',
    byMethod: 'Collections by method',
    refunds: 'Refunds',
    monthToDate: 'Month to date',
    yearToDate: 'Year to date',
    byPayer: 'By payer',
    byService: 'By service',
    byDoctor: 'By doctor',
    sixMonths: 'Panels, last six months',
    panel: 'Panel',
    claimsCount: 'Claims',
    rejected: 'Rejected',
    daysToPay: 'Days to pay',
    terms: 'Terms (days)',
    writtenOff: 'Written off',
    reason: 'Top reason',
    stockValue: 'Stock value (cost)',
    expiredBatches: 'Expired batches this month',
    unitsDispensed: 'Units dispensed',
    fastest: 'Fastest movers',
    slowest: 'Slowest movers',
    medicine: 'Medicine',
    quantity: 'Units dispensed',
    stockBody:
      'Current on-hand value and movements recorded in the selected month. No demo turnover, cost or daily-use figures are assumed.',
  },
  operations: {
    unavailable: 'Not available',
    unavailableBody:
      'This view is not connected yet. No operational figures or clinical records are shown.',
    notConfigured: 'Not configured',
    noRecords: 'No records yet',
    chooseBranch: 'Choose a branch',
    record: 'Record',
    reference: 'Reference',
    patient: 'Patient',
    status: 'Status',
    branch: 'Branch',
    visits: 'Visits',
    revenue: 'Revenue',
    wait: 'Average wait',
    adoption: 'App adoption',
    groupReports: 'Group reports',
    netCollections: 'Net collections',
    openBills: 'Open bills',
    openBalance: 'Open balance',
  },
  videoFee: {
    title: 'Video consults',
    label: 'Video consult fee (RM)',
    hint: 'One fee for every branch. Empty = patients cannot ask for a video consult.',
    current: 'Patients pay {{fee}} for a video consult.',
    notSet: 'Not set: patients cannot ask for a video consult in the app.',
    member: 'Members pay {{fee}} (set under Discount schemes).',
    note: 'Changes apply to new requests and doctor-booked video consults; bills already opened keep their price.',
    save: 'Save video consult fee',
    invalid: 'Enter the fee in RM (up to RM 1,000), or leave it empty.',
    savedOff: 'Saved: patients cannot ask for a video consult',
    saved: 'Saved: video consults {{fee}}',
    memberAbove:
      'The member price ({{fee}}) must stay below the fee. Lower it under Discount schemes first, or enter a higher fee.',
  },
  scheme: {
    services: 'Services',
    gpService: 'GP consultation',
    checkService: 'Health check-up discount',
    videoService: 'Video consultation',
    usageWindow:
      'Bills and savings are for this month. Verification counts show current records.',
    status: 'Scheme status',
    thisMonth: 'This month',
    verification: 'Verification',
    verificationBody:
      'Card and number, reviewed by Finance. No official lookup provider is connected.',
    months_one: '{{count}} month from verification',
    months_other: '{{count}} months from verification',
    rules: 'What discounts can and cannot do',
    allowed: 'Allowed',
    notAllowed: 'Not allowed',
    allowedBody:
      'Configured packages, member prices and recorded discounts. Changes apply to future patient-paid lines.',
    notAllowedBody:
      'Referral rewards and fee-splitting. No referral-reward feature is provided.',
    title: 'Member price scheme',
    live: 'Live',
    paused: 'Paused',
    scope: 'Applies to',
    scopeBody:
      'Patient-paid portion only, never the panel portion. Changes apply to future prices; recorded bills stay intact.',
    gp: 'GP consultation member price (RM)',
    check: 'Health check-up discount (%)',
    video: 'Video consultation member price (RM)',
    validity: 'Validity (months from verification)',
    wording: 'Wording shown in the app',
    edit: 'Edit scheme',
    bills: 'Member bills this month',
    savings: 'Member savings this month',
    verified: 'Verified members',
    pending: 'Pending verifications',
    invalid:
      'Enter valid prices, a percentage from 0 to 100, validity from 1 to 120 months, and wording.',
  },
  // RL-1675: the nurse dashboard.
  nurseDashboard: {
    openChats: 'Open chats',
    openChatsLink: 'Open Patient chat',
    waitingVitals: 'Waiting for vitals',
    waitingVitalsLink: 'Open the queue',
    chooseBranch: 'Choose a branch',
  },
  // RL-1673 / RL-1674: Patient chat (the nurses' inbox).
  patientChat: {
    title: 'Chats from the app',
    filter: 'Chats',
    open: 'Open',
    closed: 'Closed',
    list: 'Chats',
    conversation: 'Conversation',
    choose: 'Choose a chat to read it and reply.',
    conversationWith: 'Chat with {{name}}',
    messagesLabel: 'Messages',
    record: 'Open the patient record',
    clinic: 'Clinic',
    reply: 'Reply',
    replyHint:
      'The patient gets “New message from the clinic” in the app (never the text).',
    send: 'Send',
    sendClose: 'Send and close',
    close: 'Close conversation',
    sent: 'Reply sent',
    sentClosed: 'Reply sent and conversation closed',
    closedToast: 'Conversation closed',
    closeTitle: 'Close this conversation?',
    closeBody:
      'The chat with {{name}} closes for good: the patient’s next message starts a new one. Send any last reply first.',
    keepOpen: 'Keep it open',
    notSent: 'Not sent',
    changed: 'The chat changed: it is reloaded, check it and send again',
    closedBadge: 'Closed',
    closedNote:
      'This conversation is closed. The patient’s next message starts a new one.',
    closedDeleted:
      'Closed: the patient deleted the app account. Nobody can receive a reply.',
    identityPendingNote:
      'The app created this record and the identity is not verified yet: answer general questions only, nothing from the record.',
    outOfHoursNote:
      'Sent after 10 pm: the app told the patient that a nurse replies from 8 am.',
    chooseBranch: 'Choose a branch to see its chats.',
    noneOpen: 'No open chats. New chats from the app appear here.',
    noneClosed: 'No closed chats.',
    you: 'You: ',
    unread_one: '{{count}} unread message',
    unread_other: '{{count}} unread messages',
    messages_one: '{{count}} message',
    messages_other: '{{count}} messages',
    waiting: 'Waiting for a reply',
    answered: 'Answered',
    outOfHours: 'Sent after hours',
    identityPending: 'Identity not verified: general questions only',
    accountDeleted: 'App account deleted',
  },
  // RL-1663: after saving opening hours, the bookings left outside them.
  calendarHours: {
    title_one: '{{count}} booking falls outside the new hours',
    title_other: '{{count}} bookings fall outside the new hours',
    body_one:
      '{{count}} booking falls outside the new hours. Ask the patient to rebook?',
    body_other:
      '{{count}} bookings fall outside the new hours. Ask them to rebook?',
    friday: 'Friday prayers',
    banner_one:
      '{{count}} booking is outside the opening hours and has not been asked to rebook.',
    banner_other:
      '{{count}} bookings are outside the opening hours and have not been asked to rebook.',
    more_one: 'and {{count}} more',
    more_other: 'and {{count}} more',
    ask: 'Ask them to rebook',
    notNow: 'Not now',
    sent_one: '{{count}} patient asked to pick a new time',
    sent_other: '{{count}} patients asked to pick a new time',
    sentBody:
      'Each gets “Your booking needs a new time” in the app; the bookings show as needing a new time.',
    notSent: 'Not sent',
  },
  // RL-1665 (RL-1945): a counter complaint for a patient with the app.
  complaintForm: {
    inAppNote:
      'If the patient has the app, what happened and your replies show in the patient’s app (with “Update on your complaint” messages).',
  },
} as const;

export type Messages = typeof en;
